import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Row, Col, Dropdown, Button, Tooltip, Spin, Input } from 'antd';
import PropTypes from 'prop-types';

import { getUserId } from 'graphql/cache';
import { FETCH_PIPEDRIVE_CONTACTS } from 'graphql/queries/integrationQueries';
import images from 'constants/images';
import AddRoundIcon from 'components/Icons/AddRoundIcon';
import AddUser from 'components/Icons/AddUser';
import InfoIcon from 'components/Icons/InfoIcon';
import utils from 'utils/utils';

import './PipedriveClientName.scss';

/**
 * PipedriveClientName Component
 *
 * A dropdown component for selecting Pipedrive contacts with pagination, search, and keyboard navigation.
 * Replaces the original Ant Design Cascader with enhanced functionality including:
 * - Cursor-based pagination with infinite scroll
 * - Real-time search with intelligent sorting (name and email)
 * - Keyboard navigation (arrow keys, enter, escape)
 * - Auto-focus on search input
 * - Loading states and error handling
 * - Support for both contact name and email selection
 */
const PipedriveClientName = ({
  label,
  type,
  action,
  onChange,
  clientNameValue,
  language,
  fromTemplate,
}) => {
  // Ref for the main container
  const pipedriveClientNameRef = useRef(null);

  const [clientName, setClientName] = useState(clientNameValue);
  const [clientNames, setClientNames] = useState([]);
  const [visiblePipedriveClientName, setVisiblePipedriveClientName] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedContactId, setSelectedContactId] = useState(null);

  // Ref for the search input to enable auto-focus
  const searchInputRef = useRef(null);

  // GraphQL query for fetching Pipedrive contacts with pagination
  const [fetchPipedriveContacts] = useLazyQuery(FETCH_PIPEDRIVE_CONTACTS, {
    skip: !getUserId(), // Skip if user is not logged in
    fetchPolicy: 'network-only', // Always fetch from network, not cache
    onCompleted: ({ fetchPipedriveContacts }) => {
      const newContacts = fetchPipedriveContacts.contacts || [];
      const newNextCursor = fetchPipedriveContacts.nextCursor;

      if (nextCursor) {
        // Append new contacts to existing ones for pagination
        setClientNames((prevContacts) => [...prevContacts, ...newContacts]);
      } else {
        // First load - replace contacts
        setClientNames(newContacts);
      }

      setNextCursor(newNextCursor);
      setHasMore(!!newNextCursor); // Set hasMore based on whether nextCursor exists
      setLoading(false);
    },
    onError: (error) => {
      console.log(error);
      setLoading(false);
    },
  });

  // Fetch initial contacts when component mounts
  useEffect(() => {
    fetchPipedriveContacts({
      variables: {
        cursor: null, // Start from the beginning
        limit: 50, // Fetch 50 contacts initially
      },
    });
  }, []); // eslint-disable-line

  /**
   * Load more contacts for pagination
   * Only executes if not currently loading, has more data, and has a next cursor
   */
  const loadMoreContacts = useCallback(() => {
    if (loading || !hasMore || !nextCursor) return;

    setLoading(true);
    fetchPipedriveContacts({
      variables: {
        cursor: nextCursor, // Use the cursor from previous response
        limit: 50, // Fetch 50 more contacts
      },
    });
  }, [fetchPipedriveContacts, loading, hasMore, nextCursor]);

  /**
   * Handle scroll events for infinite scroll pagination
   * Triggers loading more contacts when user scrolls near the bottom
   */
  const handleScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      // Load more when user scrolls to bottom (with 50px threshold for better UX)
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        loadMoreContacts();
      }
    },
    [loadMoreContacts]
  );

  // Filter and sort contacts based on search term (supports both name and email search)
  const filteredContacts = clientNames
    .filter(
      (contact) =>
        contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (contact.email && contact.email.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      if (!searchTerm) return 0;

      // Prioritize exact matches over partial matches
      const aNameExact = a.name.toLowerCase() === searchTerm.toLowerCase();
      const bNameExact = b.name.toLowerCase() === searchTerm.toLowerCase();

      if (aNameExact && !bNameExact) return -1;
      if (!aNameExact && bNameExact) return 1;

      // Then prioritize name matches over email matches
      const aNameMatch = a.name.toLowerCase().includes(searchTerm.toLowerCase());
      const bNameMatch = b.name.toLowerCase().includes(searchTerm.toLowerCase());

      if (aNameMatch && !bNameMatch) return -1;
      if (!aNameMatch && bNameMatch) return 1;

      return 0;
    });

  // Reset selected index when search term changes to prevent invalid selections
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchTerm]);

  /**
   * Handle contact selection or new contact creation
   * @param {string} name - The contact name
   * @param {string} email - The contact email
   */
  const onInputChange = useCallback(
    (name, email, contactId) => {
      const limitedName = utils.limitInputLength(name);

      setClientName(limitedName);
      setSelectedContactId(contactId); // Set the selected contact ID
      setVisiblePipedriveClientName(false);
      setSelectedIndex(-1);

      if (fromTemplate) {
        // Template mode: pass name and email for template handling
        onChange(limitedName, email);
      } else {
        // Normal mode: handle contact-specific actions
        if (action === 'contact') {
          onChange({ type: 'setName', action: 'name', value: limitedName || '' });
          onChange({ type: 'setEmail', action: 'email', value: email || '' });
        }
        onChange({ type, action, value: limitedName });
      }
    },
    [onChange, fromTemplate, action, type]
  );

  /**
   * Handle keyboard navigation within the dropdown
   * Supports arrow keys for navigation, enter for selection, and escape to close
   */
  const handleKeyDown = useCallback(
    (event) => {
      if (!visiblePipedriveClientName) return;

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          // Move selection down, but don't go beyond the last item
          setSelectedIndex((prev) => (prev < filteredContacts.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          event.preventDefault();
          // Move selection up, allow going back to -1 (no selection)
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          event.preventDefault();
          // Select the currently highlighted contact or use search text
          if (selectedIndex >= 0 && selectedIndex < filteredContacts.length) {
            const selectedContact = filteredContacts[selectedIndex];
            onInputChange(selectedContact.name, selectedContact.email);
          } else if (searchTerm.trim()) {
            // If no contact is selected but there's search text, use the search text
            onInputChange(searchTerm.trim(), '');
          }
          break;
        case 'Escape':
          event.preventDefault();
          // Close the dropdown and reset selection
          setVisiblePipedriveClientName(false);
          setSelectedIndex(-1);
          break;
        default:
          break;
      }
    },
    [visiblePipedriveClientName, selectedIndex, filteredContacts, searchTerm, onInputChange]
  );

  // Custom dropdown menu with search, pagination, and keyboard navigation
  const menu = (
    <div
      className={`wix-client-name-dropdown-menu pipedrive-contact-dropdown ${
        language === 'hebrew' || language === 'arabic' ? 'language-rtl' : ''
      }`}
      onScroll={handleScroll} // Enable infinite scroll
      onKeyDown={handleKeyDown} // Enable keyboard navigation
      tabIndex={0} // Make the dropdown focusable for keyboard events
    >
      {/* Search input for filtering contacts */}
      <div className="search-container">
        <Input
          ref={searchInputRef} // Ref for auto-focus functionality
          placeholder="Search contacts..."
          value={searchTerm}
          className="pipedrive-client-name-input"
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.stopPropagation();
              setVisiblePipedriveClientName(false);
              onInputChange(!!event.target.value?.trim() ? event.target.value : '', '');
            }
          }}
          size="small"
          allowClear // Allow clearing the search input
          suffix={<AddUser />} // Add user icon for visual consistency
        />
      </div>

      {/* Render filtered contacts with keyboard navigation support */}
      {filteredContacts?.map((contact, index) => (
        <div
          key={contact.id}
          className={`contact-option ${contact.id === selectedContactId ? 'selected' : ''}`}
          onClick={() => onInputChange(contact.name, contact.email, contact.id)} // Handle contact selection
        >
          <div className="contact-name">{contact.name}</div>
          {contact.email && <div className="contact-email">{contact.email}</div>}
        </div>
      ))}

      {/* Loading indicator for pagination */}
      {loading && (
        <div className="loading-container">
          <Spin size="small" /> Loading more contacts...
        </div>
      )}

      {/* Message when all contacts have been loaded */}
      {!hasMore && clientNames.length > 0 && filteredContacts.length === clientNames.length && (
        <div className="no-more-contacts">No more contacts to load</div>
      )}

      {/* Message when no contacts are found (either no data or no search results) */}
      {filteredContacts.length === 0 && !loading && (
        <div className="no-contacts-found">
          {searchTerm ? 'No contacts match your search' : 'No contacts found'}
        </div>
      )}
    </div>
  );

  return (
    <div className="wix-client-name-dropdown-container" ref={pipedriveClientNameRef}>
      {/* Label and tooltip section */}
      {label && (
        <Row>
          <label className="input-label">
            {label}
            <span className="asterisk">*</span>
          </label>
          <Tooltip
            overlayClassName="wizard-tooltip-email"
            title="Search for a Pipedrive contact or add a new one that will be available on Prospero only."
            color="black"
            key="white">
            <InfoIcon />
          </Tooltip>
        </Row>
      )}

      {/* Main dropdown component */}
      <Dropdown
        overlay={menu} // Custom menu with search and pagination
        trigger={['click']} // Open on click only
        visible={visiblePipedriveClientName}
        onVisibleChange={(visible) => {
          setVisiblePipedriveClientName(visible);
          if (!visible) {
            // Reset state when dropdown closes
            setSearchTerm('');
            setSelectedIndex(-1);
          } else {
            // Reset selection when dropdown opens
            setSelectedIndex(-1);
            // Auto-focus search input when dropdown opens (with small delay for smooth UX)
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 100);
          }
        }}
        placement="bottomLeft"
        getPopupContainer={(trigger) => trigger.parentElement} // Ensure proper positioning
      >
        {/* Dropdown trigger button */}
        <Button
          id="wix-client-name-dropdown-button"
          onClick={() => setVisiblePipedriveClientName(!visiblePipedriveClientName)}>
          <Row justify="space-between">
            <Col>
              {/* Pipedrive contact icon */}
              <img
                alt="Pipedrive"
                className={fromTemplate ? 'wix-template' : ''}
                src={images.PIPEDRIVE_ICON}
              />
              {/* Display selected contact name or placeholder text */}
              <span className={`wix-button-text ${fromTemplate ? 'wix-template' : ''}`}>
                {clientName || 'Select/Add Contact (↵ to add)'}
              </span>
            </Col>
            <Col>
              {/* Add icon to indicate dropdown functionality */}
              <AddRoundIcon />
            </Col>
          </Row>
        </Button>
      </Dropdown>
    </div>
  );
};

// Default props for the component
PipedriveClientName.defaultProps = {
  clientNameValue: '',
  language: 'english',
};

// PropTypes for type checking and documentation
PipedriveClientName.propTypes = {
  type: PropTypes.string.isRequired, // Type of action (e.g., 'setContact')
  action: PropTypes.string.isRequired, // Action to perform (e.g., 'contact')
  onChange: PropTypes.func.isRequired, // Callback function for value changes
  clientNameValue: PropTypes.string, // Initial client name value
  language: PropTypes.string, // Language for RTL support
};

export default PipedriveClientName;
